
// username search
// first name / last name search
// username search
import React, { useEffect, useRef, useState } from "react";
import { ApiService } from "services/ApiService";
import { ArtistDto } from "models/ArtistDto";
import { useNavigate } from "react-router-dom";

const ArtistSearch: React.FC = () => {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<ArtistDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedArtist, setSelectedArtist] = useState<ArtistDto | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const [lastCompletedQuery, setLastCompletedQuery] = useState("");
    const navigate = useNavigate();

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const latestRequestIdRef = useRef(0);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const runSearch = async (query: string) => {
        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            abortControllerRef.current?.abort();
            abortControllerRef.current = null;
            latestRequestIdRef.current += 1;
            setResults([]);
            setLoading(false);
            setLastCompletedQuery("");
            return;
        }

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const requestId = (latestRequestIdRef.current += 1);
        setLoading(true);

        try {
            const response = await ApiService.searchArtistsByNameOrUsername(
                { query: trimmedQuery },
                { signal: controller.signal }
            );

            if (requestId !== latestRequestIdRef.current) return;

            if (response.success && response.data) {
                setResults(response.data);
            } else {
                setResults([]);
            }
        } catch (error: any) {
            if (requestId !== latestRequestIdRef.current) return;

            if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
                return;
            }

            setResults([]);
        } finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
                setLastCompletedQuery(trimmedQuery);
            }
        }
    };

    useEffect(() => {
        const nextQuery = search;

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        latestRequestIdRef.current += 1;

        if (!nextQuery.trim()) {
            setResults([]);
            setLoading(false);
            setLastCompletedQuery("");
            return;
        }

        debounceTimerRef.current = setTimeout(() => {
            runSearch(nextQuery);
        }, 250);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, [search]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        runSearch(search);
    };

    const filteredResults = results.filter(
        (artist) =>
            artist.firstName?.toLowerCase().includes(search.toLowerCase()) ||
            artist.lastName?.toLowerCase().includes(search.toLowerCase()) ||
            artist.userName?.toLowerCase().includes(search.toLowerCase())
    );

    const hasQuery = search.trim().length > 0;
    const showNoResults =
        hasQuery &&
        !loading &&
        search.trim() === lastCompletedQuery &&
        filteredResults.length === 0;

    const handleSelectedArtist = (artist: ArtistDto) => {
        setSelectedArtist(artist);
        navigate(`/artist/${artist.id}`);
    };

    return (
        <div className="max-w-xl mx-auto p-4">
            <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow">
                <div className="flex items-center space-x-2">
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            placeholder="Search artists..."
                            value={search}
                            onChange={handleSearchChange}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                            className="border rounded px-2 py-1 w-full"
                        />
                        {showTooltip && (
                            <div className="absolute left-0 top-full mt-1 bg-gray-700 text-white text-xs rounded px-2 py-1 shadow z-10">
                                Search for artists by name or username
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        disabled={loading}
                    >
                        {loading ? "Searching..." : "Search"}
                    </button>
                </div>
            </form>

            <div className="mt-6">
                {filteredResults.length > 0 ? (
                    <ul className="border rounded max-h-60 overflow-y-auto bg-white">
                        {filteredResults.map((artist) => (
                            <li
                                key={artist.id}
                                className={`px-4 py-2 cursor-pointer hover:bg-blue-100 ${selectedArtist?.id === artist.id ? "bg-blue-200" : ""}`}
                                onClick={() => handleSelectedArtist(artist)}
                            >
                                <div className="font-semibold">{artist.firstName} {artist.lastName}</div>
                                <div className="text-sm text-gray-600">{artist.userName}</div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    showNoResults && <div>No artists found.</div>
                )}
            </div>
        </div>
    );
};

export default ArtistSearch;